import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import RedistributeFooter from './RedistributeFooter';
import RedistributeRowHeader from './RedistributeRowHeader';
import RedistributeSidebar from './RedistributeSidebar';
import RedistributeSummaryCard from './RedistributeSummaryCard';
import RedistributeCostCodeRow from './RedistributeCostCodeRow';
import RedistributeReviewModal from './RedistributeReviewModal';
import OnTraccrButton from '../../../../../common/buttons/OnTraccrButton';
import TimeTrackingGridWizardAddCostcodeDrawer from '../Wizard/TimeTrackingGridWizardAddCostcodeDrawer';
import {
  columnsToWizardCostcodeSelections,
  mergeCostcodeSelectionsIntoGridState,
} from '../Wizard/buildWizardGridPayload';
import {
  addHours,
  clearLedgerForColumnIds,
  clearLedgerForRow,
  cloneDeep,
  getAddedCostcodeColumnRowIds,
  getRowTotals,
  getSameProjectCostcodedColumns,
  getProjectIdFromColumn,
  isHoursPositive,
  subtractHours,
  sumCellHours,
} from './redistributionHelpers';

const defaultCellValues = { hours: 0, otHours: 0, doubleOTHours: 0 };

export default function TimeTrackingGridRedistributeModal({
  id,
  responses,
  setResponses,
  columnRowId,
  initialRowId,
  visible,
  onClose,
  divisions,
  configProps,
  costcodes,
  fetchProjectCostcodes,
}) {
  const [pendingGrid, setPendingGrid] = useState(null);
  const [activeRowId, setActiveRowId] = useState(initialRowId);
  const [addCostcodeOpen, setAddCostcodeOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const ledgerRef = useRef({});

  const originalGrid = responses?.[id];

  useEffect(() => {
    if (!visible) return;
    setPendingGrid(cloneDeep(originalGrid) ?? null);
    setActiveRowId(initialRowId);
    setReviewOpen(false);
    ledgerRef.current = {};
  }, [visible, originalGrid, initialRowId]);

  const gridState = pendingGrid || {};
  const columns = gridState.columns || [];
  const values = gridState.values || [];
  const activeColumn = useMemo(
    () => columns.find((column) => column.rowId === columnRowId) || null,
    [columns, columnRowId],
  );
  const activeProjectId = getProjectIdFromColumn(activeColumn);
  const projectLabel = activeColumn?.project?.name || 'Unassigned';

  const {
    userRows,
    equipmentRows,
  } = useMemo(
    () => {
      const users = [];
      const equipment = [];
      values.forEach((row) => {
        if (row.type === 'user') users.push(row);
        else if (row.type === 'equipment') equipment.push(row);
      });
      return { userRows: users, equipmentRows: equipment };
    }, [values],
  );

  const activeRow = useMemo(
    () => values.find((row) => row.id === activeRowId) || values[0] || null,
    [values, activeRowId],
  );

  const isEquipment = activeRow?.type === 'equipment';
  const relevantRows = isEquipment ? equipmentRows : userRows;

  const activeRowIndex = useMemo(
    () => relevantRows.findIndex((row) => row.id === activeRow?.id),
    [relevantRows, activeRow],
  );

  const sameProjectCostcodedColumns = useMemo(
    () => getSameProjectCostcodedColumns(columns, activeProjectId),
    [columns, activeProjectId],
  );

  const rowTotals = useMemo(() => {
    if (!activeRow?.id || !columnRowId) return null;
    return getRowTotals({
      gridState,
      originalGridState: originalGrid,
      rowId: activeRow.id,
      unassignedColumnRowId: columnRowId,
      activeProjectId,
    });
  }, [activeRow?.id, columnRowId, gridState, originalGrid, activeProjectId]);

  const unassignedHours = rowTotals?.unassigned || defaultCellValues;
  const costCodedTotals = rowTotals?.costCoded || defaultCellValues;
  const unassignedTotal = sumCellHours(unassignedHours);
  const hasOverrun = !!rowTotals?.overrun;

  let redistributeStatus;
  let redistributeTitle;

  if (hasOverrun) {
    redistributeStatus = 'overrun';
    redistributeTitle = 'Overrun';
  } else if (isHoursPositive(unassignedTotal)) {
    redistributeTitle = 'Unassigned Hours';
    redistributeStatus = 'default';
  } else {
    redistributeTitle = 'All Distributed';
    redistributeStatus = 'complete';
  }

  const wizardSelectedCostcodes = useMemo(
    () => columnsToWizardCostcodeSelections(columns),
    [columns],
  );

  const handleClose = () => {
    ledgerRef.current = {};
    setPendingGrid(cloneDeep(originalGrid) ?? null);
    setReviewOpen(false);
    onClose();
  };

  const commitPending = () => {
    if (pendingGrid) {
      setResponses((prev) => ({
        ...prev,
        [id]: pendingGrid,
      }));
    }
    ledgerRef.current = {};
    setReviewOpen(false);
    onClose();
  };

  const reviewSummary = useMemo(() => {
    if (!columnRowId) return { remainingRows: [], overrunRows: [] };
    const remaining = [];
    const overrun = [];
    values.forEach((row) => {
      const totals = getRowTotals({
        gridState,
        originalGridState: originalGrid,
        rowId: row.id,
        unassignedColumnRowId: columnRowId,
        activeProjectId,
      });
      const rowUnassignedTotal = sumCellHours(totals.unassigned);
      if (isHoursPositive(rowUnassignedTotal)) {
        remaining.push({
          id: row.id,
          name: row.name,
          unassignedTotal: rowUnassignedTotal,
        });
      }
      if (totals.overrun) {
        overrun.push({
          id: row.id,
          name: row.name,
          overrunBy: totals.overrunBy,
        });
      }
    });
    return { remainingRows: remaining, overrunRows: overrun };
  }, [values, gridState, originalGrid, columnRowId, activeProjectId]);

  const handleDone = () => {
    const { remainingRows, overrunRows } = reviewSummary;
    if (remainingRows.length > 0 || overrunRows.length > 0) {
      setReviewOpen(true);
      return;
    }
    commitPending();
  };

  const handleResetActiveRow = () => {
    if (!activeRow?.id) return;
    const original = (originalGrid?.values || []).find((row) => row.id === activeRow.id);
    if (!original) return;

    const addedColumnRowIds = getAddedCostcodeColumnRowIds(
      originalGrid?.columns,
      pendingGrid?.columns,
    );

    ledgerRef.current = clearLedgerForRow(ledgerRef.current, activeRow.id);
    ledgerRef.current = clearLedgerForColumnIds(ledgerRef.current, addedColumnRowIds);

    setPendingGrid((prev) => {
      if (!prev) return prev;

      const addedIds = new Set(
        getAddedCostcodeColumnRowIds(originalGrid?.columns, prev.columns),
      );

      return {
        ...prev,
        columns: (prev.columns || []).filter((column) => !addedIds.has(column.rowId)),
        values: (prev.values || []).map((row) => {
          if (row.id === activeRow.id) return cloneDeep(original);

          if (!addedIds.size) return row;

          const next = { ...row };
          addedIds.forEach((columnRowId) => {
            delete next[columnRowId];
          });
          return next;
        }),
      };
    });
  };

  const adjustDestinationHours = (destinationColumnRowId, destHourType, signedDelta) => {
    if (!activeRow?.id || !columnRowId) return;

    const sameProjectColumnIds = sameProjectCostcodedColumns.map((col) => col.rowId);

    setPendingGrid((prev) => {
      if (!prev) return prev;

      let result;
      if (signedDelta > 0) {
        result = addHours({
          gridState: prev,
          ledger: ledgerRef.current,
          rowId: activeRow.id,
          unassignedColumnRowId: columnRowId,
          destinationColumnRowId,
          destHourType,
          delta: signedDelta,
          sameProjectCostcodedColumnIds: sameProjectColumnIds,
        });
      } else {
        result = subtractHours({
          gridState: prev,
          ledger: ledgerRef.current,
          rowId: activeRow.id,
          unassignedColumnRowId: columnRowId,
          destinationColumnRowId,
          destHourType,
          delta: -signedDelta,
          sameProjectCostcodedColumnIds: sameProjectColumnIds,
        });
      }

      if (!result.applied) return prev;
      ledgerRef.current = result.ledger;
      return result.gridState;
    });
  };

  const commitWizardCostcodes = (selections) => {
    setPendingGrid((prev) => {
      const current = prev || {};
      const merged = mergeCostcodeSelectionsIntoGridState({
        prevColumns: current.columns || [],
        prevValues: current.values || [],
        selections,
      });

      return {
        ...current,
        columns: merged.columns,
        values: merged.values,
      };
    });
    setAddCostcodeOpen(false);
  };

  const goToRow = (rowId) => {
    const row = values.find((r) => r.id === rowId);
    if (row) setActiveRowId(rowId);
  };

  return (
    <>
      <Modal
        visible={visible}
        onCancel={handleClose}
        title="Redistribute Hours"
        footer={null}
        width="min(1080px, 100vw)"
        className="ttg-redistribute-modal-wrap"
        destroyOnClose
        centered
      >
        <div className="ttg-redistribute-modal">
          <div className="ttg-redistribute-body">
            <RedistributeSidebar
              projectLabel={projectLabel}
              userRows={userRows}
              equipmentRows={equipmentRows}
              columnRowId={columnRowId}
              activeRowId={activeRow?.id}
              onRowSelect={setActiveRowId}
            />

            <div className="ttg-redistribute-main">
              <RedistributeRowHeader
                rowName={activeRow?.name || 'No rows'}
                projectLabel={projectLabel}
                canReset={!!activeRow}
                isEquipment={isEquipment}
                activeRowIndex={activeRowIndex}
                relevantRows={relevantRows}
                onReset={handleResetActiveRow}
                goToRow={goToRow}
              />

              <div className="ttg-redistribute-cards-row">
                <RedistributeSummaryCard
                  title={redistributeTitle}
                  redistributeStatus={redistributeStatus}
                  isEquipment={isEquipment}
                  hours={unassignedHours.hours}
                  otHours={unassignedHours.otHours}
                  doubleOTHours={unassignedHours.doubleOTHours}
                />
                <RedistributeSummaryCard
                  title="Cost Coded"
                  isEquipment={isEquipment}
                  hours={costCodedTotals.hours}
                  otHours={costCodedTotals.otHours}
                  doubleOTHours={costCodedTotals.doubleOTHours}
                />
              </div>

              <div className="ttg-redistribute-cost-code-list">
                {sameProjectCostcodedColumns.map((column) => (
                  <RedistributeCostCodeRow
                    key={column.rowId}
                    column={column}
                    cell={activeRow?.[column.rowId]}
                    isEquipment={isEquipment}
                    onHourDelta={adjustDestinationHours}
                  />
                ))}

                <OnTraccrButton
                  title="Add Cost Code"
                  titleClassName="ttg-redistribute-add-costcode-text"
                  type="secondary"
                  icon={<PlusOutlined style={{ fontSize: 20, color: '#1d4ed8' }} />}
                  iconLeft
                  className="ttg-redistribute-add-costcode"
                  onClick={() => setAddCostcodeOpen(true)}
                />
              </div>
            </div>
          </div>

          <RedistributeFooter
            projectLabel={projectLabel}
            isEquipment={isEquipment}
            totals={rowTotals}
            onDone={handleDone}
          />
        </div>
      </Modal>

      <TimeTrackingGridWizardAddCostcodeDrawer
        visible={addCostcodeOpen}
        onClose={() => setAddCostcodeOpen(false)}
        selectedCostcodes={wizardSelectedCostcodes}
        setSelectedCostcodes={commitWizardCostcodes}
        divisions={divisions}
        configProps={configProps}
        costcodes={costcodes}
        fetchProjectCostcodes={fetchProjectCostcodes}
        lockedProjectId={activeProjectId != null ? activeProjectId : undefined}
        restrictToGlobalCostcodesOnly={activeProjectId == null}
      />

      <RedistributeReviewModal
        visible={reviewOpen}
        remainingRows={reviewSummary.remainingRows}
        overrunRows={reviewSummary.overrunRows}
        onReturn={() => setReviewOpen(false)}
        onProceed={commitPending}
      />
    </>
  );
}

TimeTrackingGridRedistributeModal.propTypes = {
  id: PropTypes.string.isRequired,
  responses: PropTypes.shape({}).isRequired,
  setResponses: PropTypes.func.isRequired,
  columnRowId: PropTypes.string,
  initialRowId: PropTypes.string,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  divisions: PropTypes.arrayOf(PropTypes.string).isRequired,
  configProps: PropTypes.shape({
    isProjectRequired: PropTypes.bool,
  }).isRequired,
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  fetchProjectCostcodes: PropTypes.func,
};

TimeTrackingGridRedistributeModal.defaultProps = {
  costcodes: [],
  fetchProjectCostcodes: null,
};
